import React from 'react'

import { useUnsafeCallback } from '@factorialco/deprecated-design-system'
import { Line } from 'recharts'

import { NULL_SERIES_SUFFIX } from './helpers'
import { BarPositions, OnClickHandler, Serie } from './types'

export const ComboLine: React.FC<{
  series: Serie[]
  onClick?: OnClickHandler
  barPositions?: BarPositions
}> = ({ series, onClick, barPositions }) => {
  const renderDot = useUnsafeCallback(
    ({
      cx,
      cy,
      id,
      color,
      index,
      radius,
      serieId,
      className,
    }: {
      cx: number
      cy: number
      id: string
      index: number
      radius: number
      color?: string
      serieId: string
      className?: string
    }) => {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill={color}
          stroke="none"
          className={className}
          key={`${serieId}-${id}-${index}`}
          onClick={() => onClick?.({ index, id, serieId })}
        />
      )
    },
    [onClick]
  )

  const renderStyles = useUnsafeCallback(
    (className: string, offset: number) => (
      <style>
        {`
        .${className} {
          transform: translateX(${offset}px);
        }
      `}
      </style>
    ),
    []
  )

  return series.map(({ bars, lines, serieId }) => {
    const barIds = bars.map(({ id }) => id)
    return lines.map(({ id, color }) => {
      const className = `chart-line-${id}`
      const isNullLine = id.endsWith(NULL_SERIES_SUFFIX)

      return (
        <>
          <Line
            dataKey={id}
            fill={color}
            type="linear"
            stroke={color}
            key={`line-${id}`}
            className={className}
            connectNulls={isNullLine}
            isAnimationActive={false}
            strokeDasharray={isNullLine ? '4 4' : undefined}
            activeDot={({ cx, cy, index }) => {
              if (isNullLine) return <></>
              return renderDot({ id, cy, color, index, serieId, cx, className, radius: 4 })
            }}
            dot={({ cx, cy, index }) => {
              const barId = barIds.find(
                (currentBarId) => barPositions?.[`${serieId}-${currentBarId}`]?.[index]
              )
              const barPosition = barPositions?.[`${serieId}-${barId}`]?.[index]
              const offset = barPosition ? barPosition.x + barPosition.width / 2.0 - cx : 0
              return (
                <>
                  {index === 0 && renderStyles(className, offset)}
                  {cy !== null &&
                    renderDot({ id, cy, color, index, serieId, cx, className: '', radius: 2.5 })}
                </>
              )
            }}
          />
        </>
      )
    })
  })
}
